import {
  useFindAllLicensesQuery,
  useFindAllModulesQuery,
  useFindAllOrdersQuery,
  useFindOneLicenseQuery,
  useRemoveLicenseMutation,
} from '../../api/api-hooks';
import { Grid, TextField, Typography } from '@mui/material';
import { Autocomplete } from '@mui/material';
import ItemOptionsBar from '../../components/ItemOptionsBar';
import { nullStringCheck } from '../../utils/stringManipulation';
import { Item } from '../../utils/sharedTypes';
import { addModuleMiddleware, itemValidator } from './softwareSoldUtils';
import useLicenseItemCreator from './useLicenseItemCreator';
import useLicenseItemUpdater from './useLicenseItemUpdater';
import { License } from '../../api/api-generated';
import { useEffect, useMemo, useState } from 'react';
import { SoftwareSoldItemForm } from './softwareSoldTypes';
import ModulesManagementButton from '../modules/ModulesManagementButton';

interface Props {
  selectedSoftware: Item[];
  setSelectedSoftware: React.Dispatch<React.SetStateAction<Item[]>>;
  license: License;
}
const addFormInitialState: SoftwareSoldItemForm = {
  hardware: null,
  company: null,
  license: undefined,
  software: null,
  order: null,
  module: null,
  modulesToAdd: [],
  notes: '',
};

const SoftwareSoldModulesOptionsBar = ({
  selectedSoftware,
  setSelectedSoftware,
  license,
}: Props) => {
  const [deleteSoftware] = useRemoveLicenseMutation();
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const itemCreator = useLicenseItemCreator();
  const itemUpdater = useLicenseItemUpdater();
  const { data: orders } = useFindAllOrdersQuery({});
  const { data: modules } = useFindAllModulesQuery({});
  const { data: licenses } = useFindAllLicensesQuery({});
  useEffect(() => {
    addFormInitialState.company = license.company;
    addFormInitialState.hardware = license.hardware;
    addFormInitialState.software = license.software;
  }, [license]);
  const availableModules = useMemo(() => {
    if (licenses === undefined || modules === undefined) return [];
    const existingModules = licenses.filter(
      (module) =>
        module.companyId === license.companyId &&
        module.hardwareId === license.hardwareId &&
        module.softwareId === license.softwareId &&
        module.id !== license.id
    );
    const existingModulesIds = existingModules.map((module) => module.moduleId);
    return modules.filter((module) => !existingModulesIds.includes(module.id));
  }, [license, licenses, modules]);
  if (orders === undefined || modules === undefined || licenses === undefined)
    return null;

  const filteredOrders = orders.filter((order) => !order.completed);
  return (
    <>
      <ItemOptionsBar
        selectedItems={selectedSoftware}
        setSelectedItems={setSelectedSoftware}
        handlerMiddleware={addModuleMiddleware}
        itemValidator={itemValidator}
        formInstructions='License and software type are required.'
        addFormInitialState={addFormInitialState}
        editModalOpen={editModalOpen}
        setEditModalOpen={setEditModalOpen}
        itemName='Module'
        childrenCreator={(addFormState: SoftwareSoldItemForm, handler: any) => (
          <Grid container spacing={3}>
            <Grid container item xs direction='column' spacing={1}>
              <Grid item>
                <Typography>
                  Company: {addFormState.company?.companyName}
                </Typography>
              </Grid>
              <Grid item>
                <Typography>Software: {addFormState.software?.type}</Typography>
              </Grid>
              <Grid item>
                <Typography>
                  Hardware: {addFormState.hardware?.serial}
                </Typography>
              </Grid>
            </Grid>
            <Grid container item xs direction='column' spacing={1}>
              <Grid item>
                <Autocomplete
                  onChange={(event, value) => {
                    handler(event, value, 'order');
                  }}
                  value={addFormState.order}
                  options={filteredOrders}
                  getOptionLabel={(order) =>
                    order.purchaseOrderName
                      ? order.purchaseOrderName
                      : order.orderDate
                  }
                  style={{ width: 300 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Select Order'
                      variant='outlined'
                    />
                  )}
                />
              </Grid>
              <Grid item>
                <Autocomplete
                  onChange={(event, value) => {
                    handler(event, value, 'module');
                  }}
                  value={addFormState.module}
                  options={availableModules}
                  getOptionLabel={(order) => order.moduleName}
                  style={{ width: 300 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Select Module Type'
                      variant='outlined'
                    />
                  )}
                />
              </Grid>
              <Grid item>
                <ModulesManagementButton />
              </Grid>
              <Grid item>
                <TextField
                  margin='dense'
                  name='license'
                  label='License'
                  fullWidth
                  value={nullStringCheck(addFormState.license)}
                  onChange={handler}
                />
              </Grid>
            </Grid>
          </Grid>
        )}
        itemCreator={itemCreator}
        itemGetter={useFindOneLicenseQuery}
        itemUpdater={itemUpdater}
        itemDeleter={deleteSoftware}
      />
    </>
  );
};

export default SoftwareSoldModulesOptionsBar;
